import TokenManager from './tokenManager';

/**
 * Centralized API client that automatically uses the correct token type
 * All backend API calls should use ID tokens (which contain user email)
 */
class ApiClient {
    private static instance: ApiClient;
    private tokenManager: TokenManager;

    private constructor() {
        this.tokenManager = TokenManager.getInstance();
    }

    static getInstance(): ApiClient {
        if (!ApiClient.instance) {
            ApiClient.instance = new ApiClient();
        }
        return ApiClient.instance;
    }

    private getApiUrl(): string {
        if (process.env.REACT_APP_API_BASE_URL) {
            return process.env.REACT_APP_API_BASE_URL;
        }
        
        const environment = process.env.REACT_APP_ENVIRONMENT || 'dev';
        const applicationName = process.env.REACT_APP_APPLICATION_NAME || 'servicecatalog';
        const domain = process.env.REACT_APP_DOMAIN || 'locomotive10.com';
        
        return `https://api.${environment}.${applicationName}.${domain}`;
    }

    /**
     * Makes authenticated API calls using ID token
     * ID tokens contain user email and are required by backend functions
     */
    async fetch(endpoint: string, options: RequestInit = {}): Promise<Response> {
        const token = this.tokenManager.getIdToken();
        if (!token) {
            throw new Error('No ID token available - user not authenticated');
        }

        const url = `${this.getApiUrl()}${endpoint}`;
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            ...options.headers
        };

        // Use mockApiCall for local development
        if (process.env.NODE_ENV === 'development' && window.location.hostname === 'localhost') {
            const { mockApiCall } = await import('./mockApi');
            return mockApiCall(url, { ...options, headers });
        }

        return fetch(url, {
            ...options,
            headers
        });
    }

    /**
     * GET request helper
     */
    async get(endpoint: string): Promise<Response> {
        return this.fetch(endpoint, { method: 'GET' });
    }

    /**
     * POST request helper
     */
    async post(endpoint: string, data?: any): Promise<Response> {
        return this.fetch(endpoint, {
            method: 'POST',
            body: data ? JSON.stringify(data) : undefined
        });
    }

    /**
     * PUT request helper
     */
    async put(endpoint: string, data?: any): Promise<Response> {
        return this.fetch(endpoint, {
            method: 'PUT',
            body: data ? JSON.stringify(data) : undefined
        });
    }

    /**
     * DELETE request helper
     */
    async delete(endpoint: string): Promise<Response> {
        return this.fetch(endpoint, { method: 'DELETE' });
    }
}

export default ApiClient;